'use client';

import { EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { Layout } from '@/shared/ui/layout';
import { HeroBannerSlide } from './hero-banner-slide';
import './hero-banner.scss';

import { Media } from '@/shared/ui/media/types';
import { HeroBannerProps } from './types';
import { heroBannerVariants } from './variants';

type HeroBannerCarouselProps = HeroBannerProps;

export const HeroBannerCarousel = ({
    transition,
    data,
    variant = 'default',
    ...props
}: HeroBannerCarouselProps) => {
    const isScrollable = (data.length ?? 0) > 1;
    const direction = transition?.direction || 'ltr';
    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            loop: true,
            active: isScrollable,
            containScroll: false,
            direction,
        },
        [
            ...(transition?.delay ? [Autoplay({ delay: transition.delay })] : []),
            ...(transition?.animation === 'fade' ? [Fade()] : []),
        ],
    );
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const handleSlideButtonClick = useCallback(
        (index: number) => {
            if (!emblaApi) return;
            emblaApi.scrollTo(index);
        },
        [emblaApi],
    );
    const onInit = useCallback((emblaApi: EmblaCarouselType) => {
        setScrollSnaps(emblaApi.scrollSnapList());
    }, []);
    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;
        onInit(emblaApi);
        onSelect(emblaApi);

        emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);

        return () => {
            if (!emblaApi) return;
            emblaApi.off('reInit', onInit).off('reInit', onSelect).off('select', onSelect);
        };
    }, [emblaApi, onInit, onSelect, data.length]);

    return (
        <Layout
            className={cn(heroBannerVariants({ variant }))}
            ref={emblaRef}
            dir={direction}
            data-testid="hero-banner-carousel"
            data-variant={variant}
            {...props}
        >
            <div className="hero-banner__container" data-testid="hero-banner-container">
                {data.map((props: Media, index: number) => (
                    <HeroBannerSlide
                        key={index}
                        isScrollable={isScrollable && variant !== 'fullscreen'}
                        isInView={selectedIndex === index}
                        priority={!index}
                        {...props}
                    />
                ))}
            </div>
            <div className="hero-banner__controls" data-testid="hero-banner-controls">
                {isScrollable && (
                    <div className="hero-banner__dots" data-testid="hero-banner-dots">
                        {scrollSnaps.map((_, index) => (
                            <button
                                key={index}
                                className={cn(
                                    'hero-banner__dot',
                                    index === selectedIndex && 'hero-banner__dot--active',
                                )}
                                onClick={() => handleSlideButtonClick(index)}
                                aria-label={`Go to slide ${index + 1}`}
                                data-testid="hero-banner-dot"
                                data-slide={index + 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};
